import { addHours } from 'date-fns';
import React from 'react';
import { RecordContextProvider, useRecordContext } from 'react-admin';

import { B2B_CAMPAIGN_GRACE_PERIOD_HOURS } from '@Common/constants/b2b/campaigns';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { CampaignStatusField } from '@sh4res/bo-common/components/B2bCampaigns/CampaignStatusField';
import { CampaignTimeline } from '@sh4res/bo-common/components/B2bCampaigns/CampaignTimeline';
import { AmountsSection } from '@sh4res/bo-common/components/B2bCampaigns/show/components/dashboard/components/AmountsSection';
import { AnswerRateChart } from '@sh4res/bo-common/components/B2bCampaigns/show/components/dashboard/components/AnswerRateChart';
import { UTCDateField } from '@sh4res/bo-common/fields';

export const B2bCampaignDashboard = ({ resource }: { resource: string }) => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  if (!record) {
    return null;
  }

  return (
    <>
      <B2bCampaignDetails />
      {record.choice_collected_by === 'distributor' && (
        <AnswerRateChart id={record.id} resource={resource} />
      )}
      <AmountsSection id={record.id} resource={resource} />
    </>
  );
};

const B2bCampaignDetails = () => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  if (!record) {
    return null;
  }

  const paymentDate = record.end_date
    ? addHours(new Date(record.end_date), B2B_CAMPAIGN_GRACE_PERIOD_HOURS)
    : null;
  const paymentDateLabel =
    record.payment_method === 'sepa'
      ? 'resources.bo_common.b2b.campaigns.dashboard.payment.sepa_direct_debit_date'
      : 'resources.bo_common.b2b.campaigns.dashboard.payment.funds_request_date';

  const fields: Record<string, React.JSX.Element> = {
    status: (
      <CampaignStatusField source="status" data-testid="BoExternalB2bCampaignDetails_StatusField" />
    ),
  };

  if (record.choice_collected_by === 'distributor') {
    fields.start_date = <UTCDateField data-testid="BoExternalB2bCampaignDetails_StartDateField" />;
    fields.end_date = <UTCDateField data-testid="BoExternalB2bCampaignDetails_EndDateField" />;
    fields.payment_date = (
      <UTCDateField data-testid="BoExternalB2bCampaignDetails_DistributorPaymentDateField" />
    );
  } else {
    fields.payment_date = (
      <UTCDateField data-testid="BoExternalB2bCampaignDetails_PaymentDateField" />
    );
  }

  return (
    <ShowSection label="general.sections.details">
      <RecordContextProvider value={{ ...record, payment_date: paymentDate }}>
        <ShowFields<GetB2bCampaignOneResponse & { payment_date: Date | null }>
          fields={fields}
          labels={{
            payment_date: paymentDateLabel,
          }}
        />
        {record.choice_collected_by === 'distributor' && <CampaignTimeline record={record} />}
      </RecordContextProvider>
    </ShowSection>
  );
};
